import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Delete, Fingerprint } from "lucide-react";

/**
 * PinPad — 4-digit PIN entry with numpad. Auto-submits when 4 digits entered.
 */
export default function PinPad({ open, title, subtitle, onClose, onSubmit, error, testId = "pin-pad" }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setPin(""); }, [open]);

  useEffect(() => {
    if (pin.length === 4 && !busy) {
      setBusy(true);
      Promise.resolve(onSubmit(pin))
        .catch(() => {})
        .finally(() => { setBusy(false); setPin(""); });
    }
  }, [pin]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose?.(); return; }
      if (e.key === "Backspace") { setPin((p) => p.slice(0, -1)); return; }
      if (/^[0-9]$/.test(e.key)) { setPin((p) => (p + e.key).slice(0, 4)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const tap = (n) => setPin((p) => (p + String(n)).slice(0, 4));
  const back = () => setPin((p) => p.slice(0, -1));

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] flex items-center justify-center px-4"
        style={{ background: "rgba(3,3,5,0.72)", backdropFilter: "blur(14px)" }}
        onClick={onClose}
      >
        <motion.div
          data-testid={testId}
          initial={{ opacity: 0, scale: 0.94, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94, y: 24 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong w-full max-w-xs sm:max-w-sm rounded-3xl relative overflow-hidden p-6 sm:p-8"
        >
          <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(0,240,255,0.18)" }} />
          <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(112,0,255,0.2)" }} />

          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full glass flex items-center justify-center" data-testid={`${testId}-close`}>
            <X size={14} />
          </button>

          <div className="relative text-center">
            <div className="w-14 h-14 rounded-full mx-auto bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center">
              <Fingerprint size={22} className="text-black" />
            </div>
            <p className="mono text-[10px] uppercase tracking-[0.3em] text-white/50 mt-4">{subtitle || "Quick access"}</p>
            <h2 className="display text-2xl mt-1">{title || "Enter your 4-digit PIN"}</h2>

            {/* Dots */}
            <div className="flex justify-center gap-3 mt-6">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full border-2 transition-all ${
                    i < pin.length ? "border-cyan-400 bg-cyan-400 shadow-[0_0_16px_rgba(0,240,255,0.6)]" : "border-white/25"
                  }`}
                />
              ))}
            </div>

            {error && <div className="mt-4 mono text-[12px] text-rose-300">{error}</div>}
            {busy && !error && <div className="mt-4 mono text-[11px] text-white/50">Verifying…</div>}

            {/* Numpad */}
            <div className="mt-7 grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button
                  key={n}
                  data-testid={`${testId}-key-${n}`}
                  onClick={() => tap(n)}
                  disabled={busy}
                  className="h-14 rounded-2xl glass hover:border-cyan-400/40 hover:shadow-[0_0_20px_rgba(0,240,255,0.15)] transition-all display text-2xl disabled:opacity-50"
                >
                  {n}
                </button>
              ))}
              <div />
              <button
                data-testid={`${testId}-key-0`}
                onClick={() => tap(0)}
                disabled={busy}
                className="h-14 rounded-2xl glass hover:border-cyan-400/40 hover:shadow-[0_0_20px_rgba(0,240,255,0.15)] transition-all display text-2xl disabled:opacity-50"
              >0</button>
              <button
                data-testid={`${testId}-back`}
                onClick={back}
                disabled={busy}
                className="h-14 rounded-2xl glass hover:border-rose-400/40 hover:text-rose-300 transition-all flex items-center justify-center disabled:opacity-50"
              >
                <Delete size={18} />
              </button>
            </div>

            <p className="mono text-[10px] uppercase tracking-[0.24em] text-white/40 mt-5">Auto-submits on 4th digit</p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
